import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { confirmDelete } from '../utils/confirmDelete';
import { T, PageHeader, Alert } from '../components/UI';

export default function Staff() {
  const navigate = useNavigate();
  const [users,      setUsers]      = useState([]);
  const [roles,      setRoles]      = useState([]);
  const [locations,  setLocations]  = useState([]); // combined site + daily sheet
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [showForm,   setShowForm]   = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [resetting,  setResetting]  = useState(null);
  const [showReset,  setShowReset]  = useState(null);
  const [resetPass,  setResetPass]  = useState({});
  const [form, setForm] = useState({ name:'', email:'', password:'', role:'', site:'' });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes, siteRes, dailyRes] = await Promise.all([
        api.get('/users'),
        api.get('/users/roles'),
        api.get('/users/site-locations'),
        api.get('/jobs/locations'),
      ]);
      setUsers(usersRes.data.users);
      setRoles(rolesRes.data.roles || []);
      // Combine both location lists
      const siteNames  = (siteRes.data.locations||[]).map(l=>({ name:l.name, type:'Site' }));
      const dailyNames = (dailyRes.data.locations||[]).map(l=>({ name:l.name, type:'Daily Sheet' }));
      setLocations([...siteNames, ...dailyNames].sort((a,b)=>a.name.localeCompare(b.name)));
    } catch { setError('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.site) { setError('Location is required'); return; }
    setSaving(true); setError(null);
    try {
      if (editing) {
        await api.patch(`/users/${editing._id}`, { name:form.name, role:form.role, site:form.site });
      } else {
        if (!form.password) { setError('Password is required'); setSaving(false); return; }
        await api.post('/users', form);
      }
      setForm({ name:'', email:'', password:'', role:'', site:'' });
      setShowForm(false); setEditing(null); fetchAll();
    } catch (err) { setError(err.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const startEdit = (user) => {
    setEditing(user);
    setForm({ name:user.name||'', email:user.email||'', password:'', role:user.role||'', site:user.site||'' });
    setShowForm(true);
    window.scrollTo({top:0,behavior:'smooth'});
  };

  const cancelForm = () => { setShowForm(false); setEditing(null); setForm({ name:'', email:'', password:'', role:'', site:'' }); setError(null); };

  const handleDeactivate = async (id, name) => {
    if (!window.confirm(`Deactivate "${name}"?`)) return;
    if (!await confirmDelete()) return;
    try { await api.delete(`/users/${id}`); fetchAll(); }
    catch (err) { setError(err.response?.data?.message || 'Failed'); }
  };

  const handleReactivate = async (id) => {
    try { await api.patch(`/users/${id}`, { isActive:true }); fetchAll(); }
    catch { setError('Failed to reactivate'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Permanently delete "${name}"? This cannot be undone.`)) return;
    if (!await confirmDelete()) return;
    try { await api.delete(`/users/${id}/permanent`); fetchAll(); }
    catch (err) { setError(err.response?.data?.message || 'Failed to delete'); }
  };

  const handleResetPassword = async (id) => {
    const pwd = resetPass[id];
    if (!pwd || pwd.length < 6) { setError('Password must be at least 6 characters'); return; }
    setResetting(id);
    try {
      await api.post(`/users/${id}/reset-password`, { password:pwd });
      setShowReset(null); setResetPass(p=>({...p,[id]:''}));
    } catch { setError('Failed to reset password'); }
    finally { setResetting(null); }
  };

  const getRoleLabel = (roleName) => roles.find(r=>r.name===roleName)?.label || roleName;

  return (
    <div style={T.page}>
      <PageHeader title="Admin Staff" onBack={()=>navigate(-1)} actions={
        <button onClick={()=>{ cancelForm(); setShowForm(s=>!s); }} style={T.btnPrimary}>
          <i className="ti ti-plus" aria-hidden="true"/> Add Admin Staff
        </button>
      }/>
      <div style={{ maxWidth:'800px', margin:'0 auto', padding:'16px 16px 80px' }}>
        {error && <Alert type="error">{error}</Alert>}

        {showForm && (
          <div style={{ background:'var(--surface)', border:`1px solid ${editing?'var(--amber)':'var(--blue)'}`, borderRadius:'var(--radius-lg)', padding:'18px', marginBottom:'16px' }}>
            <div style={{ fontSize:'13px', fontWeight:'500', color:editing?'var(--amber)':'var(--blue)', marginBottom:'14px' }}>
              {editing ? `Edit: ${editing.name}` : 'Add New Admin Staff Member'}
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px,1fr))', gap:'10px', marginBottom:'12px' }}>
                <div>
                  <label style={T.label}>Full Name *</label>
                  <input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}
                    style={T.input} placeholder="John Smith" required
                    onFocus={e=>e.target.style.borderColor='var(--blue)'} onBlur={e=>e.target.style.borderColor='var(--border-md)'}/>
                </div>
                {!editing && (
                  <div>
                    <label style={T.label}>Email *</label>
                    <input type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))}
                      style={T.input} placeholder="john@carsaloon.com.au" required
                      onFocus={e=>e.target.style.borderColor='var(--blue)'} onBlur={e=>e.target.style.borderColor='var(--border-md)'}/>
                  </div>
                )}
                {!editing && (
                  <div>
                    <label style={T.label}>Password *</label>
                    <input type="password" value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))}
                      style={T.input} placeholder="Min. 6 characters" required
                      onFocus={e=>e.target.style.borderColor='var(--blue)'} onBlur={e=>e.target.style.borderColor='var(--border-md)'}/>
                  </div>
                )}
                <div>
                  <label style={T.label}>Role *</label>
                  <select value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value}))} style={T.input} required>
                    <option value="">— Select Role —</option>
                    {roles.map(r=><option key={r._id} value={r.name}>{r.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={T.label}>Location *</label>
                  <select value={form.site} onChange={e=>setForm(f=>({...f,site:e.target.value}))} style={{ ...T.input, borderColor: !form.site && saving ? 'var(--red)' : 'var(--border-md)' }} required>
                    <option value="">— Select Location —</option>
                    {locations.map(l=><option key={l.name} value={l.name}>{l.name} ({l.type})</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end' }}>
                <button type="button" onClick={cancelForm} style={T.btnSecondary}>Cancel</button>
                <button type="submit" disabled={saving} style={{ ...T.btnPrimary, background:editing?'var(--amber)':'var(--blue)', opacity:saving?0.6:1 }}>
                  {saving ? 'Saving...' : editing ? 'Save Changes' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? <div style={{ textAlign:'center', padding:'40px', color:'var(--muted)' }}>Loading...</div> : (
          <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
            {users.map(u=>(
              <div key={u._id} style={{ background:'var(--surface)', border:`1px solid ${u.isActive?'var(--border)':'rgba(163,45,45,0.2)'}`, borderRadius:'var(--radius-lg)', padding:'14px 16px', opacity:u.isActive?1:0.7 }}>
                <div style={{ display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap' }}>
                  <div style={{ width:'40px', height:'40px', borderRadius:'50%', background:'var(--blue-light)', border:'2px solid var(--blue)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'14px', fontWeight:'500', color:'var(--blue)', flexShrink:0 }}>
                    {u.name?.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
                      <span style={{ fontWeight:'500', color:'var(--text)', fontSize:'14px' }}>{u.name}</span>
                      <span style={{ background:'var(--blue-light)', color:'var(--blue)', fontSize:'11px', fontWeight:'500', padding:'2px 8px', borderRadius:'20px' }}>{getRoleLabel(u.role)}</span>
                      {!u.isActive && <span style={{ background:'var(--red-bg)', color:'var(--red)', fontSize:'11px', fontWeight:'500', padding:'2px 8px', borderRadius:'20px' }}>Deactivated</span>}
                    </div>
                    <div style={{ fontSize:'12px', color:'var(--muted)', marginTop:'2px' }}>{u.email}</div>
                    {u.site && <div style={{ fontSize:'11px', color:'var(--hint)', marginTop:'1px' }}>{u.site}</div>}
                  </div>
                  <div style={{ display:'flex', gap:'6px', flexShrink:0, flexWrap:'wrap' }}>
                    <button onClick={()=>startEdit(u)} style={{ ...T.btnIcon, color:'var(--blue)', borderColor:'rgba(10,110,180,0.25)', fontSize:'12px', padding:'5px 10px' }}>
                      <i className="ti ti-edit" style={{ fontSize:'13px' }} aria-hidden="true"/> Edit
                    </button>
                    <button onClick={()=>setShowReset(showReset===u._id?null:u._id)} style={{ ...T.btnIcon, color:'var(--purple)', borderColor:'rgba(83,74,183,0.25)', fontSize:'12px', padding:'5px 10px' }}>
                      <i className="ti ti-key" style={{ fontSize:'13px' }} aria-hidden="true"/> Reset
                    </button>
                    {u.isActive
                      ? <button onClick={()=>handleDeactivate(u._id, u.name)} style={{ ...T.btnIcon, color:'var(--amber)', borderColor:'rgba(133,79,11,0.25)', fontSize:'12px', padding:'5px 10px' }}>
                          <i className="ti ti-user-off" style={{ fontSize:'13px' }} aria-hidden="true"/> Deactivate
                        </button>
                      : <button onClick={()=>handleReactivate(u._id)} style={{ ...T.btnIcon, color:'var(--green)', borderColor:'rgba(15,110,86,0.25)', fontSize:'12px', padding:'5px 10px' }}>
                          <i className="ti ti-user-check" style={{ fontSize:'13px' }} aria-hidden="true"/> Reactivate
                        </button>
                    }
                    <button onClick={()=>handleDelete(u._id, u.name)} style={{ ...T.btnIcon, color:'var(--red)', borderColor:'rgba(163,45,45,0.25)', fontSize:'12px', padding:'5px 10px' }}>
                      <i className="ti ti-trash" style={{ fontSize:'13px' }} aria-hidden="true"/> Delete
                    </button>
                  </div>
                </div>
                {showReset===u._id && (
                  <div style={{ marginTop:'10px', display:'flex', gap:'8px', alignItems:'center', padding:'10px', background:'var(--purple-bg)', borderRadius:'var(--radius)', border:'1px solid rgba(83,74,183,0.2)' }}>
                    <input type="password" value={resetPass[u._id]||''} onChange={e=>setResetPass(p=>({...p,[u._id]:e.target.value}))}
                      style={{ ...T.input, flex:1 }} placeholder="New password (min 6 chars)"
                      onFocus={e=>e.target.style.borderColor='var(--purple)'} onBlur={e=>e.target.style.borderColor='var(--border-md)'}/>
                    <button onClick={()=>handleResetPassword(u._id)} disabled={resetting===u._id}
                      style={{ ...T.btnPrimary, background:'var(--purple)', opacity:resetting===u._id?0.6:1 }}>
                      {resetting===u._id?'Saving...':'Save'}
                    </button>
                    <button onClick={()=>setShowReset(null)} style={T.btnSecondary}>✕</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
